import React from "react"
import { Link, useLocation } from "react-router-dom"
import { toHomePath } from "../../helpers/routePaths"
// Direct @radix-ui/react-popover import is intentional: NavItem uses Popover.Close
// to auto-dismiss the flyout on link click — a compound-API feature not exposed
// by AppPopover's monolithic (trigger + children) wrapper.
import * as Popover from "@radix-ui/react-popover"
import { ChevronDown } from "../ui/Icon"

/**
 * @typedef {{ to: string, label: string }} FlyoutChild
 *
 * @param {{ to: string, label: string, activeMenu: string, activeKeys?: string[], children?: FlyoutChild[], onNavigate?: () => void }} props
 */
const NavItem = ({
  to,
  label,
  activeMenu,
  activeKeys,
  children,
  onNavigate,
}) => {
  const { pathname } = useLocation()
  const isActive = activeKeys
    ? activeKeys.includes(activeMenu)
    : activeMenu === to
  const navSlug = to.replaceAll("/", "-")

  if (!children || children.length === 0) {
    return (
      <li>
        <Link
          to={toHomePath(to)}
          className={`nav-item-link${isActive ? " active" : ""}`}
          aria-current={isActive ? "page" : undefined}
          onClick={onNavigate}
          data-testid={`nav-link-${navSlug}`}
        >
          {label}
        </Link>
      </li>
    )
  }

  // Determine which flyout child is active (longest pathname match)
  const siblingPaths = children.map((c) => toHomePath(c.to))
  const activeFlyoutPath = (() => {
    const matched = siblingPaths
      .filter((p) => pathname.startsWith(p))
      .sort((a, b) => b.length - a.length)
    return matched.length > 0 ? matched[0] : null
  })()

  return (
    <li>
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            className={`nav-item-link nav-item-parent${isActive ? " active" : ""}`}
            data-testid={`nav-parent-${navSlug}`}
          >
            {label}
            <ChevronDown
              size={12}
              className="nav-item-caret"
              aria-hidden="true"
            />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="nav-flyout"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            {children.map((child) => {
              const childFullPath = toHomePath(child.to)
              const isFlyoutActive = activeFlyoutPath === childFullPath
              return (
                <Popover.Close key={child.to} asChild>
                  <Link
                    to={childFullPath}
                    className={`nav-flyout-item${isFlyoutActive ? " active" : ""}`}
                    onClick={onNavigate}
                    data-testid={`nav-flyout-${child.to.replaceAll("/", "-")}`}
                  >
                    {child.label}
                  </Link>
                </Popover.Close>
              )
            })}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </li>
  )
}

export default NavItem
